<template>
  <div
    class="color-picker__container"
    @click.stop
  >
    <template v-if="mode === modes.SWATCHES">
      <div class="color-picker">
        <div class="color-picker__swatches">
          <span
            class="color-picker__item color-picker__item--picker"
            @click.stop="changeMode(modes.WHEEL)"
          />
          <template v-for="(color, index) in colors">
            <span
              :key="'fill-' + index"
              class="color-picker__item"
              :class="colorClasses({color, isDefaultColor: true})"
              :style="{'background-color': color}"
              @click.stop.prevent="selectColorSpot(color)"
            />
          </template>
        </div>
        <hr class="color-picker__sep">
        <div class="color-picker__history-title">
          Recently Used
        </div>
        <div class="color-picker__swatches">
          <template v-for="(color, index) in historyColors">
            <span
              :key="'last-selected-' + index"
              class="color-picker__item"
              :class="colorClasses({color})"
              :style="{'background-color': color}"
              @click.stop.prevent="selectColorSpot(color)"
            />
          </template>
          <!-- <span class="color-picker__item color-picker__item--disabled" /> -->
        </div>
      </div>
    </template>
    <template v-if="mode === modes.WHEEL">
      <color-wheel @input="selectColor($event)" />
      <color-picker-button
        icon="check"
        label="Apply"
        @click="apply()"
      />

      <color-picker-button
        icon="cancel"
        label="Cancel"
        @click="cancel()"
      />
    </template>
  </div>
</template>

<script>
import chroma from 'chroma-js';
import colors from './colors';
import ColorWheel from './ColorWheel';
import ColorPickerButton from './ColorPickerButton';

const modes = {
  SWATCHES: 'swatches',
  WHEEL: 'wheel',
};

export default {
  name: 'ColorPicker',
  components: {
    ColorWheel,
    ColorPickerButton,
  },
  props: {
    currentColor: {
      type: String,
      default: '',
    },
    historyColors: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    this.modes = modes; // Non-reactive
    return {
      colorSelected: '',
      savedColor: '', // This color is saved when we change modes. Is used to restore the previous color.
      mode: modes.SWATCHES,
      colors, // Colors printed in the swatch spots.
    };
  },
  methods: {
    isDefaultColor(color) {
      return this.colors.includes(color);
    },
    changeMode(mode) {
      // if we are changing to wheel, we save the current color in case the user Cancel the action.
      if (mode === this.modes.WHEEL) {
        this.savedColor = this.colorSelected;
      }
      this.mode = mode;
      console.log('change mode');
    },
    selectColor(color) {
      this.colorSelected = color;
      this.$emit('select', color);
    },
    unselectColor() {
      this.colorSelected = '';
      this.$emit('unselect');
    },
    selectColorSpot(color) {
      if (color === this.currentColor) {
        this.unselectColor();
        this.$emit('save', '#FFFFFF');
      } else {
        this.selectColor(color);
        this.$emit('save', color);
      }
    },
    apply() {
      this.selectColor(this.colorSelected);
      this.$emit('save', this.colorSelected);
      this.changeMode(modes.SWATCHES);
    },
    cancel() {
      this.selectColor(this.savedColor);
      this.changeMode(modes.SWATCHES);
    },
    colorClasses({ color, isDefaultColor }) {
      const canBeSelected = (isDefaultColor) ? true : !this.isDefaultColor(this.currentColor);
      return {
        'color-picker__item--selected': color === this.currentColor && canBeSelected,
        'color-picker__item--dark': this.isDark(color),
        'color-picker__item--light': !this.isDark(color),
      };
    },
    isDark(color) {
      if (chroma(color).luminance() < 0.5) {
        return true;
      }
      return false;
    },
  },
};
</script>

<style lang="scss" scoped>
$swatchWidth: 20px;
.color-picker{
    line-height: 0;
    margin: 0 auto;

    &__container{
      width: 280px;
      // box-shadow: 0px 4px 4px rgba(51, 51, 51, 0.1);
      border-radius: 8px;
      background-color: $gray-light;
      padding: 20px;
    }

    &__swatches{
      display: grid;
      grid-template-columns: repeat(10, $swatchWidth);
      column-gap: 4px;
      row-gap: 10px;
      min-height: 20px;
    }

    &__item{
        display: inline-block;
        width: $swatchWidth;
        height: $swatchWidth;
        cursor: pointer;
        border-radius: 100px;
        box-shadow: 0 0 1px inset $gray-light-2;
        position: relative;

        &:after{
            position: absolute;
            left: 0;
            top: 0;
            display: none;
            content: "";
            width: 20px;
            height: 20px;
            background-repeat: no-repeat;
            background-position: center center;
          }

        &--selected {
          &:after{
            display: block;
          }
        }

        &--dark {
          &:after{
            background-image: url("~@/assets/icons/check-light.svg");
          }
        }

        &--light {
          &:after{
            background-image: url("~@/assets/icons/check-dark.svg");
          }
        }

        &--picker{
          box-shadow: none;
          background: url(~@/assets/color-picker.png);
          background-size: $swatchWidth;
        }
    }

    &__item--disabled{
        cursor: default;
    }

    &__sep{
        margin: 15px 0;
    }

    &__history-title{
      min-height: 12px;
      font-size: 10px;
      font-family: $font-family-sans-serif-medium;
      color: $gray;
    }
}
</style>
